import React, {useState} from "react";
import {Button, DialogContent, DialogTitle, Grid} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {api} from "../../../../api/API";
import {AutoComplete} from "../../shopdrawing/openings/AutoComplete";
import {Opening, DrawingWallLabel} from "../../../../api/Openings";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {useFilePicker} from "../../shopdrawing/extras/FilePicker";
import {useAsyncAction} from "nate-react-api-helpers";
import useWindowSize from "react-use/lib/useWindowSize";
import {useIsQuote} from "../QuoteContainer";
import {useAlternative} from "../alternative/Alternative";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {getAll} from "../../../logistics/routes/useGetAll";
import {ext} from "../../../../api/Files";

export function DrawingWallLabelPopup<T extends (Opening|QuoteOpening|DrawingWallLabel)>(props: {
    anchor: any;
    row: T;
    width: number;
    initialValue: string;
    onCancel(): void;
    onDone(value: T): Promise<any>;
}) {
    const filePicker = useFilePicker({
        accept: ["application/pdf", "image/*"]
    });

    const [name, setName] = useState(props.row.drawingWallLabel);
    const [file, setFile] = useState(props.row.drawingWallLabelFile ? {name: props.row.drawingWallLabelFileName, id: props.row.drawingWallLabelFile} : null);
    const projectId = useProjectId();
    const isQuote = useIsQuote();
    const alt = useAlternative();

    const onDoneRef = useSyncedRef(props.onDone);
    const doneCb = useAsyncAction(async (input) => {
        await onDoneRef.current(input)
    }, []);

    const upload = useAsyncAction(async (file: File) => {
        const fileObj = await api.files.upload({
            file: file,
            // use guid naming to prevent collisions
            name: window.crypto.randomUUID() + ext(file.name),
            convert2pdf: true,
        });

        setFile({
            id: fileObj.id,
            name: file.name,
        });
    }, [filePicker]);

    const [isRename, setIsRename] = useState(false);
    const size = useWindowSize();

    const submit = () => {
        doneCb.callback(Object.assign({}, props.row, {
            drawingWallLabel: name,
            drawingWallLabelFile: file ? file.id : null,
            drawingWallLabelRenameOnlyFlag: isQuote ? isRename : undefined,
        }))
    };

    return (
        <Dialog2 open onClose={props.onCancel} fullWidth maxWidth="lg" onEnterPress={submit}>
            <DialogTitle>Wall Type</DialogTitle>
            <DialogContent>
                <div style={{height: 8}} />
                <Grid container spacing={1}>
                    <Grid item xs={8}>
                        {file ? <iframe
                            src={api.files.downloadLink(file.id, "inline")}
                            style={{
                                height: size.height * 0.75,
                                width: "100%",
                                border: "1px solid black",
                            }}
                        ></iframe> : <div>Choose a file on the right</div>}
                    </Grid>
                    <Grid item xs={4}>
                        <AutoComplete<DrawingWallLabel>
                            fullWidth
                            label="Name"
                            size="small"
                            search={async input => {

                                let result = await getAll(null, offset =>
                                    isQuote ? api.quoteOpenings.listDrawingWallLabels({
                                            project: projectId,
                                            alternative: alt,
                                            offset: offset,
                                        }) :
                                    api.openings.listDrawingWallLabels({
                                        project: projectId,
                                        offset: offset,
                                    }),
                                    {
                                        useLazy: true,
                                    })

                                if(input !== "") {
                                    const value = input;
                                    input = input.toLowerCase();
                                    result = result.filter(r => r.name.toLowerCase().indexOf(input) !== -1)

                                    if(file && isQuote) {
                                        if (!result.find(v => v.name.toLowerCase() === input.toLowerCase())) {
                                            result.push({
                                                id: Date.now(),
                                                name: "Update to '" + value + "'",
                                                value: value,
                                                isUpdateName: true,
                                            } as any)
                                        }
                                    }
                                }

                                return result;
                            }}
                            value={{name: name, fileId: file}}
                            equals={(a, b) => a.name === b.name}
                            makeLabel={a => a.name}
                            onSelect={value => {
                                if("isUpdateName" in value && value.isUpdateName) {
                                    // @ts-ignore
                                    setName(value.value);
                                    setIsRename(true);
                                    return;
                                }

                                setIsRename(false);
                                setFile(value.file ? {
                                    id: value.file,
                                    name: value.fileName || "",
                                } : null);

                                setName(value.name);
                            }}
                            onCreate={async name => {
                                setName(name);
                                setFile(null);
                            }}
                        />

                        <div style={{height: 24}} />

                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs>
                                {file ? file.name : "No file selected"}
                            </Grid>
                            <Grid item>
                                <Button variant="outlined" size="small" onClick={async () => {
                                    const files = await filePicker();
                                    upload.callback(files[0])
                                }}>
                                    Choose File
                                </Button>
                            </Grid>
                            {file && <Grid item onClick={() => setFile(null)}>
                                <Button variant="outlined" size="small">
                                    Clear File
                                </Button>
                            </Grid>}
                        </Grid>
                    </Grid>
                </Grid>

            </DialogContent>
            <SmoothActionBar
                loading={doneCb.loading}
                error={doneCb.error}
                actionName="Done"
                onCancel={props.onCancel}
                onSubmit={submit}
            />
        </Dialog2>
    )
}